
import {
  computed, defineComponent, onMounted, reactive, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import Tooltip from 'primevue/tooltip';
import useVuelidate from '@vuelidate/core';
import { required, minLength, maxLength } from '@vuelidate/validators';
import StepShell from '@/components/onboarding-v2/shared/StepShell.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import categories from '@/configs/shop-categories';
import currencies from '@/configs/currencies';
import fetchShopInformation, { updateShop } from '@/composables/shop/Shops';
import updateShopsLang from '@/composables/shop/ShopsLang';
import { fetchShopConfigurations } from '@/composables/shop/MyShopParameters';
import { SaveShopsConfiguration, saveShopParamsOnRedis } from '@/composables/shop/ShopsConfiguration';
import { UserState, Me } from '@/composables/User';
import { showToastError } from '@/helpers';

interface Field {
  name: string;
  shopCategory: any;
  currency: string;
  defaultLang: any;
  privacyPolicyUrl: Record<string, string>;
  dataManagerEmail: Record<string, string>;
}

export default defineComponent({
  name: 'ShopConfirmStep',

  components: {
    StepShell,
    Dropdown,
    InputText,
    LocalizedTextInput,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    canGoBack: { type: Boolean, default: false },
  },

  emits: ['next', 'back'],

  setup(_, { emit }) {
    const { t } = useI18n();

    const field = reactive<Field>({
      name: '',
      shopCategory: null,
      currency: '',
      defaultLang: null,
      privacyPolicyUrl: {},
      dataManagerEmail: {},
    });

    const shopLanguages = ref<Array<{ label: string; value: string }>>([]);
    const loading = ref(true);
    const saving = ref(false);

    const rules = computed(() => ({
      name: { required, minLength: minLength(3), maxLength: maxLength(25) },
      shopCategory: { required },
      currency: { required },
      defaultLang: { required },
    }));

    const v$ = useVuelidate(rules, field);

    onMounted(async () => {
      try {
        await Me(false);
        const shopId = UserState.activeShop?.id ?? 0;
        const info = await fetchShopInformation({ shopId });
        if (info) {
          const defaultLang = info.defaultLang ?? 'fr';
          const langs = (info.langs ?? [])
            .filter((l: any) => !l.default)
            .map((l: any) => ({ label: l.lang, value: l.lang }));
          langs.unshift({ label: defaultLang, value: defaultLang });
          if (!langs.length) {
            langs.push({ label: 'fr', value: 'fr' });
          }
          shopLanguages.value = langs;

          const rgpd = await fetchShopConfigurations({ shopId }, 'shopRequiredDataRgpd');
          const emptyByLang = () => shopLanguages.value.reduce(
            (acc: Record<string, string>, l) => { acc[l.value] = ''; return acc; },
            {},
          );
          const reduceLang = (items: any[] | null | undefined) => {
            const base = emptyByLang();
            if (!items?.length) return base;
            items.forEach((cur: any) => { base[cur.lang] = cur.value; });
            return base;
          };

          field.name = info.name ?? '';
          field.shopCategory = info.idCategoryShop ?? null;
          field.currency = info.currency ?? '';
          field.defaultLang = defaultLang;
          field.privacyPolicyUrl = reduceLang(
            rgpd?.filter((i: any) => i.key === 'shopRequiredDataRgpd_privacyPolicyUrl'),
          );
          field.dataManagerEmail = reduceLang(
            rgpd?.filter((i: any) => i.key === 'shopRequiredDataRgpd_dataManagerEmail'),
          );
        }
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        loading.value = false;
      }
    });

    const handleNext = async () => {
      const ok = await v$.value.$validate();
      if (!ok) return;
      saving.value = true;
      const shopId = UserState.activeShop?.id ?? 0;
      try {
        await updateShop([{
          id_shop: shopId,
          name: field.name,
          id_category_shop: field.shopCategory,
          currency: field.currency,
        }]);
        await updateShopsLang(
          shopLanguages.value.map((item) => ({
            id_shop: shopId,
            lang: item.value,
            default: item.value === field.defaultLang,
          })),
        );
        const configs = Object.keys(field.privacyPolicyUrl).map((k) => ({
          key: 'shopRequiredDataRgpd_privacyPolicyUrl',
          lang: k,
          value: field.privacyPolicyUrl[k],
        })).concat(Object.keys(field.dataManagerEmail).map((k) => ({
          key: 'shopRequiredDataRgpd_dataManagerEmail',
          lang: k,
          value: field.dataManagerEmail[k],
        })));
        await SaveShopsConfiguration({ id_shop: shopId, configs });
        await saveShopParamsOnRedis(shopId);
        emit('next');
      } catch {
        await showToastError('GENERIC_ERROR');
      } finally {
        saving.value = false;
      }
    };

    return {
      t,
      field,
      v$,
      categories,
      currencies,
      shopLanguages,
      loading,
      saving,
      handleNext,
    };
  },
});
